import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import { templateBadgeMapping } from "../widgets/SearchBox/capabilitySearchMappings";
import { VNode } from "src/interfaces/interfaces";
import {
  AppType_Examples,
  AppType_to_T9NKey,
} from "../widgets/AppTypeInfoModal/support";
import { tsx } from "@arcgis/core/widgets/support/widget";
import request from "@arcgis/core/request";
import { getBaseUrl } from "./utils";
import Sanitizer from "@esri/arcgis-html-sanitizer";

export const sanitizer = new Sanitizer();

export function isMobileView(): boolean {
  return window.innerWidth <= 600;
}

export function urlToObject(): Object {
  const query = (window.location.search || "?").substr(1),
    map = {};
  const urlRE = /([^&=]+)=?([^&]*)(?:&+|$)/g;
  query.replace(urlRE, (match, key, value) => {
    map[key] = this.stripStringTags(decodeURIComponent(value));
    return "";
  });
  return map;
}

export function stripStringTags(value: string): string {
  const tagsRE = /<\/?[^>]+>/g;
  return value.replace(tagsRE, "");
}

export function objectToArray(value: Object): any {
  const resultArray = Object.keys(value).map((idx) => {
    return value[idx];
  });
  return resultArray;
}

export function includes(arr: any[], str: string) {
  return arr.indexOf(str) > -1;
}

export function filterOut(arr1: any[], arr2: any[]) {
  return arr1.filter((val) => {
    return arr2.indexOf(val) === -1;
  });
}

export function concatUnique(arr1: any[], arr2: any[]) {
  const newArr = [].concat(arr1 || []); // shallow copy
  arr2.forEach((val) => {
    if (val && newArr.indexOf(val) === -1) {
      newArr.push(val);
    }
  });
  return newArr;
}

export function parseUrl(url: string): any {
  const a = document.createElement("a");
  a.href = url;
  return {
    source: url,
    protocol: a.protocol.replace(":", ""),
    host: a.hostname || window.location.host,
    port: a.port,
    query: a.search,
    params: (function () {
      const ret = {},
        seg = a.search.replace(/^\?/, "").split("&"),
        len = seg.length;
      let i = 0,
        s;
      for (; i < len; i++) {
        if (!seg[i]) {
          continue;
        }
        s = seg[i].split("=");
        ret[s[0]] = s[1];
      }
      return ret;
    })(),
    file: (a.pathname.match(/\/([^\/?#]+)$/i) || [, ""])[1],
    hash: a.hash.replace("#", ""),
    path: a.pathname.replace(/^([^\/])/, "/$1"),
    relative: (a.href.match(/tps?:\/\/[^\/]+(.+)/) || [, ""])[1],
    segments: a.pathname.replace(/^\//, "").split("/"),
  };
}

export function showNode(node) {
  const domNode =
    typeof node === "string" ? document.querySelector(node) : node;
  if (domNode) {
    domNode.classList.remove("hide");
  }
}

export function hideNode(node) {
  const domNode =
    typeof node === "string" ? document.querySelector(node) : node;
  if (domNode) {
    domNode.classList.add("hide");
  }
}

export function toggleNode(node) {
  const showHide =
    node && node.classList.contains("hide") ? "showNode" : "hideNode";
  this[showHide](node);
}

export function isNodeVisible(node) {
  return node && node.classList.contains("hide") ? false : true;
}

export function find(arr: any[], fn: Function) {
  let value;
  for (let i = 0; i < arr.length; i++) {
    value = arr[i];
    if (fn(value)) {
      return value;
    }
  }
  return null;
}

export function isRTL() {
  return document.getElementsByTagName("html")[0].getAttribute("dir") === "rtl";
}

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

let _isDarkMode: boolean;
export function isDarkMode(): "dark" | "light" {
  if (_isDarkMode != null) {
    return _isDarkMode ? "dark" : "light";
  }
  _isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return _isDarkMode ? "dark" : "light";
}

export function findAppTypeBadge(
  templateType: EAppTemplateType
): string | null {
  return templateBadgeMapping[templateType];
}

export function keyboardAccessibleHelper(callback: Function, e: KeyboardEvent) {
  if (e.code === "Enter" || e.code === "Space") {
    e.preventDefault();
    callback();
  }
}

export function isNoResourceInQueryString(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return !(
    urlParams.has("webmap") ||
    urlParams.has("webscene") ||
    urlParams.has("group")
  );
}

export function isUserAccountPublic(portal: __esri.Portal): boolean {
  return isEnterprise(portal)
    ? false
    : (portal?.user as any)?.accountType === "Public";
}

export function isEnterprise(portal: __esri.Portal): boolean {
  return portal?.isPortal;
}

export function renderDescription(
  appTypeUrlFragment: string,
  appMessages: any,
  portal: __esri.Portal
): VNode[] {
  const { appDescriptions } = appMessages || {};
  let descAppType = AppType_to_T9NKey[appTypeUrlFragment];
  if (isEnterprise(portal)) {
    // desc differs on Enterprise
    if (appTypeUrlFragment === EAppTemplateType.Insets) {
      descAppType = "insetsEnterprise";
    } else if (appTypeUrlFragment === EAppTemplateType.Streamflow) {
      descAppType = "streamflowEnterprise";
    }
  }

  const descKey = `${descAppType}Desc`;
  return [
    <h4>{appDescriptions?.sectionTitle}</h4>,
    <p innerHTML={appDescriptions?.[descKey]}></p>,
  ];
}

export function renderExamples(
  appTypeUrlFragment: string,
  appMessages: any,
  portal: __esri.Portal
): VNode[] {
  const { appExamples } = appMessages || {};
  const exKeyRoot = `${AppType_to_T9NKey[appTypeUrlFragment]}Ex`;
  const exampleLinks = AppType_Examples[appTypeUrlFragment];

  let renderList: any[] = Object.keys(appExamples)
    .filter((key) => key.indexOf(exKeyRoot) !== -1)
    .map((exKey, index) => {
      return [
        <ul>
          <li>{appExamples[exKey]}</li>
        </ul>,
        !isEnterprise(portal) && exampleLinks?.[index] != null ? (
          <calcite-button
            styles={{ "margin-left": "5px" }}
            icon-start="launch"
            appearance="transparent"
            title={appMessages.myApps.viewApp}
            onclick={() => {
              window.open(sanitizer.sanitizeUrl(exampleLinks?.[index]));
            }}
          ></calcite-button>
        ) : null,
      ];
    });

  return [
    <h4>{appExamples.sectionTitle}</h4>,
    <div class="example-grid">{renderList}</div>,
  ];
}

export async function areBetaAppsBlocked(
  portal: __esri.Portal
): Promise<boolean> {
  const response = await request(
    `${getBaseUrl(portal)}/sharing/rest/portals/self/signinSettings?f=json`
  );
  return response.data.blockBetaApps;
}
